:root {
  --font-sans: "Segoe UI Variable", "Segoe UI", Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "Cascadia Code", "SFMono-Regular", Consolas, monospace;

  --radius-xs: 6px;
  --radius-sm: 9px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 22px;

  --shadow-xs: 0 1px 2px rgb(14 22 36 / 0.06);
  --shadow-sm: 0 5px 16px rgb(14 22 36 / 0.08);
  --shadow-md: 0 12px 32px rgb(14 22 36 / 0.12);
  --shadow-lg: 0 24px 64px rgb(14 22 36 / 0.18);

  --brand-navy-950: #071224;
  --brand-navy-900: #0b1b33;
  --brand-navy-800: #112a4b;
  --brand-navy-700: #173a64;
  --brand-navy-600: #214e80;
  --brand-gold-700: #9c6f20;
  --brand-gold-600: #b9862c;
  --brand-gold-500: #d1a24e;
  --brand-gold-400: #e3bd72;
  --brand-gold-300: #efd49a;

  --blue-600: #2f6fed;
  --blue-500: #3c82f6;
  --cyan-600: #1188ae;
  --cyan-500: #17a0c8;
  --teal-600: #0d8c83;
  --teal-500: #10a398;
  --emerald-600: #178657;
  --emerald-500: #20a86b;
  --lime-600: #628b28;
  --lime-500: #78a936;
  --amber-600: #b87515;
  --amber-500: #d69228;
  --orange-600: #c45b20;
  --orange-500: #e1702f;
  --rose-600: #b94461;
  --rose-500: #d95a76;
  --red-600: #c6414e;
  --red-500: #e0525f;
  --violet-600: #7447d6;
  --violet-500: #8b5cf6;
  --indigo-600: #5157cc;
  --indigo-500: #6670e8;
  --slate-blue-600: #50698f;
  --slate-blue-500: #617da7;

  --phase-lead: var(--violet-500);
  --phase-design: var(--indigo-500);
  --phase-estimate: var(--blue-500);
  --phase-preconstruction: var(--cyan-500);
  --phase-selections: var(--teal-500);
  --phase-contract: var(--emerald-500);
  --phase-procurement: var(--lime-500);
  --phase-build: var(--amber-500);
  --phase-punch: var(--orange-500);
  --phase-closeout: var(--rose-500);
  --phase-warranty: var(--slate-blue-500);
  --phase-neutral: #7b8798;
}

:root,
:root[data-theme="light"] {
  --canvas: #f4f6f9;
  --canvas-warm: #f7f5ef;
  --surface: #ffffff;
  --surface-subtle: #f7f9fb;
  --surface-raised: #ffffff;
  --surface-interactive: #eef2f7;
  --surface-hover: #e7edf4;
  --surface-selected: #e7f5f2;
  --surface-inverse: #0b1628;

  --text-primary: #101722;
  --text-secondary: #4b596b;
  --text-muted: #748195;
  --text-faint: #9aa5b4;
  --text-inverse: #f8fafc;

  --border-subtle: #e5e9ef;
  --border-default: #d7dde6;
  --border-strong: #bcc6d3;

  --brand: var(--brand-gold-600);
  --brand-hover: var(--brand-gold-700);
  --brand-soft: #f5ead4;
  --brand-contrast: #251a08;

  --action-primary: var(--teal-600);
  --action-primary-hover: #08756f;
  --action-positive: var(--emerald-600);
  --action-positive-hover: #126d48;
  --action-link: var(--blue-600);
  --action-link-hover: #2458bc;
  --action-ai: var(--violet-600);
  --action-ai-hover: #5d34b5;
  --action-destructive: var(--red-600);
  --action-destructive-hover: #a8323f;

  --info: var(--blue-600);
  --info-soft: #e8f0ff;
  --success: var(--emerald-600);
  --success-soft: #e5f5ed;
  --warning: var(--amber-600);
  --warning-soft: #fff0d6;
  --danger: var(--red-600);
  --danger-soft: #fde9eb;
  --ai: var(--violet-600);
  --ai-soft: #f0eaff;

  --focus-ring: #2679e8;
  --scrim: rgb(8 18 32 / 0.52);
}

:root[data-theme="dark"] {
  --canvas: #090d13;
  --canvas-warm: #0d1014;
  --surface: #111720;
  --surface-subtle: #151c26;
  --surface-raised: #19212d;
  --surface-interactive: #202a37;
  --surface-hover: #283443;
  --surface-selected: #173c39;
  --surface-inverse: #f4f7fb;

  --text-primary: #f2f5f9;
  --text-secondary: #bdc7d4;
  --text-muted: #8d9aaa;
  --text-faint: #687688;
  --text-inverse: #0b111a;

  --border-subtle: #202a36;
  --border-default: #2d3947;
  --border-strong: #465466;

  --brand: var(--brand-gold-400);
  --brand-hover: var(--brand-gold-300);
  --brand-soft: #342b1a;
  --brand-contrast: #160f05;

  --action-primary: #40c7b8;
  --action-primary-hover: #69d9cd;
  --action-positive: #4acb8a;
  --action-positive-hover: #70dda4;
  --action-link: #6ea4ff;
  --action-link-hover: #8fbbff;
  --action-ai: #b091ff;
  --action-ai-hover: #c4adff;
  --action-destructive: #ff7b84;
  --action-destructive-hover: #ff9aa1;

  --info: #6ea4ff;
  --info-soft: #16294b;
  --success: #4acb8a;
  --success-soft: #143625;
  --warning: #efb553;
  --warning-soft: #3d2d13;
  --danger: #ff7b84;
  --danger-soft: #432126;
  --ai: #b091ff;
  --ai-soft: #2d2347;

  --phase-lead: #a986ff;
  --phase-design: #858cff;
  --phase-estimate: #6ea4ff;
  --phase-preconstruction: #4bc7e7;
  --phase-selections: #45cabd;
  --phase-contract: #4acb8a;
  --phase-procurement: #9fc85b;
  --phase-build: #efb553;
  --phase-punch: #f58a4e;
  --phase-closeout: #ed7893;
  --phase-warranty: #86a2cb;
  --phase-neutral: #8d9aaa;

  --focus-ring: #6ea4ff;
  --scrim: rgb(0 0 0 / 0.68);

  --shadow-xs: 0 1px 2px rgb(0 0 0 / 0.22);
  --shadow-sm: 0 6px 20px rgb(0 0 0 / 0.24);
  --shadow-md: 0 16px 40px rgb(0 0 0 / 0.32);
  --shadow-lg: 0 30px 80px rgb(0 0 0 / 0.42);
}

html {
  font-family: var(--font-sans);
  background: var(--canvas);
  color: var(--text-primary);
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
}

body {
  margin: 0;
  min-width: 320px;
  min-height: 100vh;
  background: var(--canvas);
  color: var(--text-primary);
}

::selection {
  background: color-mix(in oklab, var(--action-primary) 28%, transparent);
}

:focus-visible {
  outline: 3px solid color-mix(in oklab, var(--focus-ring) 70%, transparent);
  outline-offset: 2px;
}

.phase-lead { --phase: var(--phase-lead); }
.phase-design { --phase: var(--phase-design); }
.phase-estimate { --phase: var(--phase-estimate); }
.phase-preconstruction { --phase: var(--phase-preconstruction); }
.phase-selections { --phase: var(--phase-selections); }
.phase-contract { --phase: var(--phase-contract); }
.phase-procurement { --phase: var(--phase-procurement); }
.phase-build { --phase: var(--phase-build); }
.phase-punch { --phase: var(--phase-punch); }
.phase-closeout { --phase: var(--phase-closeout); }
.phase-warranty { --phase: var(--phase-warranty); }
.phase-neutral { --phase: var(--phase-neutral); }
